/**
 * Real-time notifications: Socket.IO (Node) or Django Channels (WebSocket).
 */

import { io, type Socket } from 'socket.io-client';
import type { AppNotification } from '../components/Preview/api';
import { formatNotificationDisplayMessage } from './formatNotificationMessage';
import { isNodeBackend, socketIoOrigin } from './backendConfig';
import {
  connectNotificationSocket,
  attachNotificationHeartbeat,
  type V1SocketHandler,
} from './realtime';
import {
  notificationSpeakKey,
  shouldSpeakNotification,
  speakNotificationOnce,
} from './speakNotification';
import { toastOnce } from './toastOnce';
import { REALTIME_NOTIFICATION_EVENTS } from './realtimeEvents';

export type V1SocketPayload = Record<string, unknown>;

export type V1SocketClient = {
  readonly id: string;
  readonly connected: boolean;
  on(event: string, handler: V1SocketHandler): V1SocketClient;
  off(event: string, handler?: V1SocketHandler): V1SocketClient;
  disconnect(): void;
};

const NODE_SOCKET_EVENTS = [
  'newMessage',
  'newQuestion',
  'chatRequest',
  'chatRequestAccepted',
  'chatRequestDeclined',
  'questionAnswered',
  'referenceConfirmed',
  'profileAccessRequest',
  'messageRequestUpdate',
  'meeting_request_created',
  'meeting_request_updated',
  'meeting_request_rescheduled',
  'profileLike',
  'notification',
] as const;

/** Sender id from a chat `newMessage` socket payload (Node embeds full message under `data`). */
export function getRealtimeMessageSenderId(payload: V1SocketPayload): number | null {
  const data = payload.data as Record<string, unknown> | undefined;
  const sender = data?.sender as Record<string, unknown> | undefined;
  const raw = data?.senderId ?? sender?.id ?? payload.senderId ?? payload.sender_id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function isOwnRealtimeMessage(
  payload: V1SocketPayload,
  userId: number | undefined
): boolean {
  if (!userId) return false;
  const senderId = getRealtimeMessageSenderId(payload);
  return senderId !== null && senderId === Number(userId);
}

/** True when the event is from someone else (or sender unknown — legacy payloads). */
export function isIncomingRealtimeMessage(
  payload: V1SocketPayload,
  userId: number | undefined
): boolean {
  return !isOwnRealtimeMessage(payload, userId);
}

function getQuestionParticipantIds(row: Record<string, unknown> | undefined): {
  candidateId: number | null;
  recruiterId: number | null;
} {
  if (!row) return { candidateId: null, recruiterId: null };
  const candidate = row.candidate as Record<string, unknown> | undefined;
  const recruiter = row.recruiter as Record<string, unknown> | undefined;
  const candidateId = Number(row.candidateId ?? candidate?.id ?? row.candidate_id);
  const recruiterId = Number(row.recruiterId ?? recruiter?.id ?? row.recruiter_id);
  return {
    candidateId: Number.isFinite(candidateId) && candidateId > 0 ? candidateId : null,
    recruiterId: Number.isFinite(recruiterId) && recruiterId > 0 ? recruiterId : null,
  };
}

/** Skip recruiter-facing question echoes for the user who initiated the action. */
export function isOwnQuestionRealtimeEcho(
  eventName: string,
  payload: V1SocketPayload,
  userId: number | undefined
): boolean {
  if (!userId) return false;
  const row = payload.data as Record<string, unknown> | undefined;
  const { candidateId, recruiterId } = getQuestionParticipantIds(row);
  const uid = Number(userId);

  if (eventName === 'questionAnswered' || eventName === 'question_updated') {
    return candidateId !== null && candidateId === uid;
  }
  if (eventName === 'newQuestion') {
    return recruiterId !== null && recruiterId === uid;
  }
  return false;
}

/** Normalize push payloads into header notification shape. */
export function v1PayloadToNotification(payload: V1SocketPayload): AppNotification {
  const type = payload.type != null ? String(payload.type) : undefined;
  const name = payload.name != null ? String(payload.name) : undefined;
  const rawMessage = String(payload.message ?? payload.content ?? '');
  const actionUrl =
    payload.action_url != null
      ? String(payload.action_url)
      : payload.actionUrl != null
        ? String(payload.actionUrl)
        : undefined;

  const notificationId = payload.notification_id ?? payload.notificationId ?? payload.id ?? '';

  return {
    id: String(notificationId) || String(Date.now()),
    message: formatNotificationDisplayMessage({
      message: rawMessage,
      type,
      name,
    }),
    isRead: Boolean(payload.isRead ?? payload.is_read ?? false),
    createdAt: String(payload.createdAt ?? payload.created_at ?? new Date().toISOString()),
    type,
    action_url: actionUrl,
  };
}

const handlerSets: Map<string, Set<V1SocketHandler>> = new Map();
const connectHandlers: Set<() => void> = new Set();

let djangoNotificationSocket: WebSocket | null = null;
let djangoNotificationIntentionalDisconnect = false;
let djangoNotificationReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let djangoNotificationReconnectAttempt = 0;
let nodeSocket: Socket | null = null;
let nodeSocketUserId: number | null = null;
let notificationUserId: number | null = null;
let notificationHeartbeatStop: (() => void) | null = null;

const NOTIF_RECONNECT_BASE_MS = 1000;
const NOTIF_RECONNECT_MAX_MS = 30000;

function stopNotificationHeartbeat() {
  notificationHeartbeatStop?.();
  notificationHeartbeatStop = null;
}

function startNotificationHeartbeat(sock: WebSocket) {
  stopNotificationHeartbeat();
  notificationHeartbeatStop = attachNotificationHeartbeat(sock);
}

function djangoNotificationReconnectDelayMs(attempt: number): number {
  const exp = Math.min(NOTIF_RECONNECT_MAX_MS, NOTIF_RECONNECT_BASE_MS * 2 ** attempt);
  return exp + Math.floor(Math.random() * 400);
}

function clearDjangoNotificationReconnectTimer() {
  if (djangoNotificationReconnectTimer) {
    clearTimeout(djangoNotificationReconnectTimer);
    djangoNotificationReconnectTimer = null;
  }
}

function scheduleDjangoNotificationReconnect() {
  if (djangoNotificationIntentionalDisconnect || djangoNotificationReconnectTimer) return;
  const delay = djangoNotificationReconnectDelayMs(djangoNotificationReconnectAttempt);
  djangoNotificationReconnectAttempt += 1;
  djangoNotificationReconnectTimer = setTimeout(() => {
    djangoNotificationReconnectTimer = null;
    djangoNotificationSocket = null;
    ensureDjangoNotificationSocket();
  }, delay);
}

const SPEAK_SOCKET_EVENTS = new Set([
  'newQuestion',
  'newMessage',
  'chatRequest',
  'profileAccessRequest',
  'chatRequestAccepted',
  'chatRequestDeclined',
  'questionAnswered',
  'profileLike',
]);

const V1_TOAST_EVENTS = new Set<string>(REALTIME_NOTIFICATION_EVENTS);
const recentDispatchAt = new Map<string, number>();
const DISPATCH_DEDUPE_MS = 5000;

function realtimeToastType(payload: V1SocketPayload): 'success' | 'info' | 'warning' | 'error' {
  const t = String(payload.notification_type || 'success').toLowerCase();
  if (t === 'warning') return 'warning';
  if (t === 'error') return 'error';
  if (t === 'info') return 'info';
  return 'success';
}

/** One toast per WS event (avoids duplicate handlers in header components). */
function maybeShowRealtimeToast(eventName: string, payload: V1SocketPayload) {
  if (!V1_TOAST_EVENTS.has(eventName)) return;
  if (
    eventName === 'newMessage' &&
    !isIncomingRealtimeMessage(payload, notificationUserId ?? undefined)
  ) {
    return;
  }
  if (isOwnQuestionRealtimeEcho(eventName, payload, notificationUserId ?? undefined)) {
    return;
  }

  const notif = v1PayloadToNotification(payload);
  if (!notif.message) return;
  const dedupeKey = notificationSpeakKey(eventName, payload);
  toastOnce(notif.message, realtimeToastType(payload), dedupeKey);
}

function maybeSpeakNotification(eventName: string, payload: V1SocketPayload) {
  if (eventName === 'notification') {
    return;
  }

  if (
    eventName === 'newMessage' &&
    !isIncomingRealtimeMessage(payload, notificationUserId ?? undefined)
  ) {
    return;
  }
  if (isOwnQuestionRealtimeEcho(eventName, payload, notificationUserId ?? undefined)) {
    return;
  }

  const record = payload as Record<string, unknown>;
  if (SPEAK_SOCKET_EVENTS.has(eventName) || shouldSpeakNotification(record)) {
    speakNotificationOnce(eventName, record);
  }
}

function dispatch(eventName: string, payload: V1SocketPayload) {
  const dedupeKey = notificationSpeakKey(eventName, payload);
  const now = Date.now();
  const lastDispatch = recentDispatchAt.get(dedupeKey) ?? 0;
  const skipSurfaceEffects = now - lastDispatch < DISPATCH_DEDUPE_MS;
  if (!skipSurfaceEffects) {
    recentDispatchAt.set(dedupeKey, now);
    maybeShowRealtimeToast(eventName, payload);
    maybeSpeakNotification(eventName, payload);
  }

  const handlers = handlerSets.get(eventName);
  handlers?.forEach((fn) => fn(payload));
}

function fireConnect() {
  connectHandlers.forEach((fn) => fn());
}

function mapNodePayload(eventName: string, payload: V1SocketPayload) {
  if (eventName === 'messageRequestUpdate') {
    const status = String(payload.status || '');
    if (status === 'accepted') {
      dispatch('chatRequestAccepted', payload);
    } else if (status === 'declined') {
      dispatch('chatRequestDeclined', payload);
    }
    return;
  }
  if (eventName === 'notification') {
    const type = String(payload.notification_type || payload.type || 'info');
    if (type.includes('question')) {
      dispatch('newQuestion', payload);
    } else {
      dispatch('notification', payload);
    }
    return;
  }
  dispatch(eventName, payload);
}

function abandonDjangoNotificationSocket() {
  if (!djangoNotificationSocket) return;
  const sock = djangoNotificationSocket;
  djangoNotificationSocket = null;
  stopNotificationHeartbeat();
  sock.onclose = null;
  sock.onopen = null;
  try {
    sock.close();
  } catch {
    /* ignore close errors */
  }
}

function disconnectDjangoNotificationSocket() {
  djangoNotificationIntentionalDisconnect = true;
  clearDjangoNotificationReconnectTimer();
  abandonDjangoNotificationSocket();
}

function disconnectNodeSocket() {
  nodeSocket?.removeAllListeners();
  nodeSocket?.disconnect();
  nodeSocket = null;
  nodeSocketUserId = null;
}

function ensureDjangoNotificationSocket(): WebSocket | null {
  djangoNotificationIntentionalDisconnect = false;
  const existing = djangoNotificationSocket;
  if (
    existing &&
    (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)
  ) {
    return existing;
  }
  if (existing) {
    abandonDjangoNotificationSocket();
  }

  djangoNotificationSocket = connectNotificationSocket({
    newMessage: (p) => dispatch('newMessage', p),
    newQuestion: (p) => dispatch('newQuestion', p),
    question_updated: (p) => dispatch('question_updated', p),
    questionAnswered: (p) => dispatch('questionAnswered', p),
    chatRequest: (p) => dispatch('chatRequest', p),
    chatRequestAccepted: (p) => dispatch('chatRequestAccepted', p),
    chatRequestDeclined: (p) => dispatch('chatRequestDeclined', p),
    referenceConfirmed: (p) => dispatch('referenceConfirmed', p),
    profileAccessRequest: (p) => dispatch('profileAccessRequest', p),
    meeting_request_created: (p) => dispatch('meeting_request_created', p),
    meeting_request_updated: (p) => dispatch('meeting_request_updated', p),
    meeting_request_rescheduled: (p) => dispatch('meeting_request_rescheduled', p),
    profileLike: (p) => dispatch('profileLike', p),
    candidateAnalyticsUpdated: (p) => dispatch('candidateAnalyticsUpdated', p),
    notification: (p) => {
      const type = String(p.notification_type || 'info');
      if (type.includes('question')) dispatch('newQuestion', p);
      else dispatch('notification', p);
    },
  });

  if (djangoNotificationSocket) {
    const sock = djangoNotificationSocket;
    const prevOnOpen = sock.onopen;
    sock.onopen = (ev) => {
      djangoNotificationReconnectAttempt = 0;
      prevOnOpen?.call(sock, ev);
      startNotificationHeartbeat(sock);
      fireConnect();
    };
    sock.onclose = () => {
      stopNotificationHeartbeat();
      djangoNotificationSocket = null;
      if (!djangoNotificationIntentionalDisconnect) {
        scheduleDjangoNotificationReconnect();
      }
    };
    if (sock.readyState === WebSocket.OPEN) {
      fireConnect();
    }
  }

  return djangoNotificationSocket;
}

if (typeof document !== 'undefined' && !isNodeBackend()) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || djangoNotificationIntentionalDisconnect) {
      return;
    }

    const sock = djangoNotificationSocket;
    if (sock?.readyState === WebSocket.OPEN) {
      startNotificationHeartbeat(sock);
      return;
    }
    if (sock?.readyState === WebSocket.CONNECTING) {
      return;
    }

    djangoNotificationReconnectAttempt = 0;
    clearDjangoNotificationReconnectTimer();
    ensureDjangoNotificationSocket();
  });
}

if (typeof window !== 'undefined' && !isNodeBackend()) {
  window.addEventListener('online', () => {
    if (djangoNotificationIntentionalDisconnect) return;

    const sock = djangoNotificationSocket;
    if (sock?.readyState === WebSocket.OPEN || sock?.readyState === WebSocket.CONNECTING) {
      return;
    }

    djangoNotificationReconnectAttempt = 0;
    clearDjangoNotificationReconnectTimer();
    ensureDjangoNotificationSocket();
  });
  window.addEventListener('beforeunload', () => {
    disconnectSocket();
  });
}

function ensureNodeSocket(userId: number): Socket | null {
  if (nodeSocket && nodeSocketUserId === userId && nodeSocket.connected) {
    return nodeSocket;
  }

  if (nodeSocket) {
    disconnectNodeSocket();
  }

  nodeSocketUserId = userId;
  nodeSocket = io(socketIoOrigin(), {
    query: { userId: String(userId) },
    transports: ['websocket', 'polling'],
    withCredentials: true,
  });

  nodeSocket.on('connect', () => fireConnect());

  nodeSocket.on('disconnect', () => {
    if (nodeSocket?.id === undefined) {
      nodeSocket = null;
      nodeSocketUserId = null;
    }
  });

  for (const eventName of NODE_SOCKET_EVENTS) {
    nodeSocket.on(eventName, (payload: V1SocketPayload) => {
      mapNodePayload(eventName, payload);
    });
  }

  nodeSocket.io.on('reconnect', () => fireConnect());

  return nodeSocket;
}

function ensureTransport(userId?: number): void {
  if (isNodeBackend()) {
    if (userId) ensureNodeSocket(userId);
    return;
  }
  ensureDjangoNotificationSocket();
}

function createClient(transportId: string): V1SocketClient {
  const client: V1SocketClient = {
    id: transportId,
    get connected() {
      if (isNodeBackend()) {
        return nodeSocket?.connected ?? false;
      }
      return djangoNotificationSocket?.readyState === WebSocket.OPEN;
    },
    on(event: string, handler: V1SocketHandler) {
      if (event === 'connect') {
        connectHandlers.add(handler as () => void);
        if (client.connected) {
          (handler as () => void)();
        }
        return client;
      }
      if (!handlerSets.has(event)) handlerSets.set(event, new Set());
      handlerSets.get(event)!.add(handler);
      return client;
    },
    off(event: string, handler?: V1SocketHandler) {
      if (event === 'connect') {
        if (handler) connectHandlers.delete(handler as () => void);
        else connectHandlers.clear();
        return client;
      }
      if (!handler) {
        handlerSets.delete(event);
        return client;
      }
      handlerSets.get(event)?.delete(handler);
      return client;
    },
    disconnect() {
      disconnectSocket();
    },
  };
  return client;
}

export const initSocket = (userId: number): V1SocketClient => {
  notificationUserId = userId;
  ensureTransport(userId);
  return createClient(isNodeBackend() ? 'socket.io' : 'django-channels');
};

export const getSocket = (): V1SocketClient => {
  if (!isNodeBackend()) {
    ensureDjangoNotificationSocket();
  }
  return createClient(isNodeBackend() ? 'socket.io' : 'django-channels');
};

export function getNotificationWebSocket(): WebSocket | null {
  if (isNodeBackend()) return null;
  ensureDjangoNotificationSocket();
  const sock = djangoNotificationSocket;
  return sock?.readyState === WebSocket.OPEN ? sock : null;
}

export const disconnectSocket = () => {
  notificationUserId = null;
  disconnectDjangoNotificationSocket();
  disconnectNodeSocket();
  handlerSets.clear();
  connectHandlers.clear();
  recentDispatchAt.clear();
};
